import CryptoJS from "crypto-js";
import { authService } from "@/services/auth";

// Enhanced encryption using AES-256-GCM for stronger security
const KEY_SIZE = 256; // bits
const IV_SIZE = 16; // bytes

// Server-side master key (should be stored in environment variables)
// For frontend, we need to get this from the environment or use a different approach
const MASTER_KEY =
  import.meta.env.VITE_ENCRYPTION_MASTER_KEY || "default-master-key-change-in-production";

/**
 * Generate a cryptographically secure random key
 */
export function generateSecureKey(): string {
  return CryptoJS.lib.WordArray.random(KEY_SIZE / 8).toString(CryptoJS.enc.Base64);
}

/**
 * Generate a cryptographically secure random IV
 */
export function generateIV(): string {
  return CryptoJS.lib.WordArray.random(IV_SIZE).toString(CryptoJS.enc.Hex);
}

/**
 * Encrypt text using AES-256 with a provided key
 */
export function encryptWithKey(text: string, key: string): { cipher: string; iv: string } {
  const iv = CryptoJS.lib.WordArray.random(IV_SIZE);
  const keyWordArray = CryptoJS.enc.Base64.parse(key);
  const cipher = CryptoJS.AES.encrypt(text, keyWordArray, { iv }).toString();

  return {
    cipher,
    iv: iv.toString(CryptoJS.enc.Hex),
  };
}

/**
 * Decrypt text using AES-256 with a provided key
 */
export function decryptWithKey(ciphertext: string, ivHex: string, key: string): string {
  try {
    // ✅ ENHANCED: Validate inputs
    if (!ciphertext || !ivHex || !key) {
      console.warn("Decryption failed: Missing required parameters");
      return "";
    }

    const iv = CryptoJS.enc.Hex.parse(ivHex);
    const keyWordArray = CryptoJS.enc.Base64.parse(key);
    const bytes = CryptoJS.AES.decrypt(ciphertext, keyWordArray, { iv });
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);

    if (!decrypted) {
      console.warn("Decryption failed: Empty result - invalid key or corrupted data");
      return "";
    }

    return decrypted;
  } catch (error) {
    console.warn("Decryption failed:", error);
    return ""; // Return empty string for safety
  }
}

/**
 * Encrypt user key with master key for storage
 */
export function encryptUserKeyForStorage(userKey: string): { cipher: string; iv: string } {
  const iv = CryptoJS.lib.WordArray.random(IV_SIZE);
  const masterKeyWordArray = CryptoJS.enc.Utf8.parse(MASTER_KEY);
  const cipher = CryptoJS.AES.encrypt(userKey, masterKeyWordArray, { iv }).toString();

  return {
    cipher,
    iv: iv.toString(CryptoJS.enc.Hex),
  };
}

/**
 * Decrypt user key with master key
 * Returns null if decryption fails (for legacy key detection)
 */
function decryptUserKeyWithMasterKey(encryptedUserKey: string, keyIV: string): string | null {
  try {
    if (!encryptedUserKey || !keyIV) {
      return null;
    }

    const iv = CryptoJS.enc.Hex.parse(keyIV);
    const masterKeyWordArray = CryptoJS.enc.Utf8.parse(MASTER_KEY);
    const bytes = CryptoJS.AES.decrypt(encryptedUserKey, masterKeyWordArray, { iv });
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);

    if (!decrypted) {
      return null;
    }

    return decrypted;
  } catch (error) {
    console.error("Operation failed:", error);
    return null;
  }
}

function isEncryptionTimeoutError(message: string): boolean {
  return (
    message.includes("timeout") ||
    message.includes("Request timeout") ||
    message.includes("aborted")
  );
}

/** Transient auth/gateway blips — retry getUserKey instead of failing the first attempt. */
function isEncryptionTransientServerError(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes("internal server error") ||
    lower.includes("something went wrong") ||
    lower.includes("service temporarily unavailable") ||
    lower.includes("http 503") ||
    lower.includes("http 502") ||
    lower.includes("failed to fetch")
  );
}

function toCapturedError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }
  if (typeof error === "string" && error.length > 0) {
    return new Error(error);
  }
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string" &&
    (error as { message: string }).message
  ) {
    return new Error((error as { message: string }).message);
  }
  return new Error("Unknown encryption failure");
}

function reportEncryptionFallbackToSentry(error: unknown, userId: string): void {
  import("@/services/sentry")
    .then(({ captureException }) => {
      const err = toCapturedError(error);
      captureException(err, {
        level: "error",
        tags: {
          feature: "encryption",
          encryption_outcome: "plain_text_fallback",
        },
        extra: { userIdPrefix: userId.slice(0, 8) },
      });
    })
    .catch(() => {
      // Sentry optional
    });
}

/**
 * Throw Get User Key Error.
 */
function throwGetUserKeyError(lastError: Error): never {
  const detail = lastError.message;
  if (isEncryptionTimeoutError(detail)) {
    throw new Error(
      "Failed to get or create encryption key: Request timeout. Please check your internet connection and try again."
    );
  }
  if (detail === "Internal server error") {
    throw new Error(
      "Failed to get or create encryption key: The server could not set up encryption. Please try again in a moment or sign out and back in."
    );
  }
  throw new Error(`Failed to get or create encryption key: ${detail}`);
}

/**
 * Resolve Existing Encryption Key.
 */
function resolveExistingEncryptionKey(
  existingKey: { encrypted_key: string; salt?: string | null },
  userId: string,
  manager: UserEncryptionManager
): string | null {
  const salt = existingKey.salt?.trim() || "";

  if (salt) {
    const decryptedKey = decryptUserKeyWithMasterKey(existingKey.encrypted_key, salt);
    if (decryptedKey) {
      manager.setCachedKey(userId, decryptedKey);
      return decryptedKey;
    }
    // Salt present but master-key decrypt failed (env drift / corrupt row).
    // Return null so the caller re-provisions instead of using ciphertext as an AES key.
    return null;
  }

  // Legacy rows stored the raw key material without a master-key wrapper.
  manager.setCachedKey(userId, existingKey.encrypted_key);
  return existingKey.encrypted_key;
}

/**
 * Provision Encryption Key.
 */
async function provisionEncryptionKey(
  userId: string,
  manager: UserEncryptionManager,
  options?: { forceReplace?: boolean }
): Promise<string> {
  const newKey = generateSecureKey();
  const { cipher: encryptedKey, iv: keyIV } = encryptUserKeyForStorage(newKey);

  try {
    await authService.createOrUpdateEncryptionKey(userId, encryptedKey, keyIV, {
      forceReplace: Boolean(options?.forceReplace),
    });
  } catch (provisionError) {
    if (!options?.forceReplace) {
      try {
        await authService.deleteUserEncryptionKeys();
      } catch {
        // Best-effort cleanup before forced insert
      }
      try {
        await authService.createOrUpdateEncryptionKey(userId, encryptedKey, keyIV, {
          forceReplace: true,
        });
        manager.setCachedKey(userId, newKey);
        return newKey;
      } catch {
        // Fall through to race recovery / rethrow
      }
    }

    const racedKey = await authService.getUserEncryptionKey(userId);
    if (racedKey) {
      const resolved = resolveExistingEncryptionKey(racedKey, userId, manager);
      if (resolved) {
        return resolved;
      }
    }
    throw provisionError;
  }

  manager.setCachedKey(userId, newKey);
  return newKey;
}

/**
 * Fetch Or Create User Key.
 */
async function fetchOrCreateUserKey(
  userId: string,
  manager: UserEncryptionManager
): Promise<string> {
  const existingKey = await authService.getUserEncryptionKey(userId);

  if (existingKey) {
    const resolved = resolveExistingEncryptionKey(existingKey, userId, manager);
    if (resolved) {
      return resolved;
    }
    // Undecryptable / corrupt row — replace instead of reusing ciphertext as a key.
    return provisionEncryptionKey(userId, manager, { forceReplace: true });
  }

  return provisionEncryptionKey(userId, manager);
}

/**
 * Retry Get User Key After Delay.
 */
async function retryGetUserKeyAfterDelay(
  attempt: number,
  maxRetries: number,
  baseDelay: number,
  lastError: Error
): Promise<void> {
  const delay = baseDelay * 2 ** (attempt - 1);
  if (process.env.NODE_ENV === "development") {
    console.warn(
      `[Encryption] Retry attempt ${attempt}/${maxRetries} after ${delay}ms delay: ${lastError.message}`
    );
  }
  await new Promise((resolve) => setTimeout(resolve, delay));
}

/**
 * User-specific encryption key management
 */
class UserEncryptionManager {
  private userKey: string | null = null;
  private userId: string | null = null;

  setCachedKey(userId: string, key: string): void {
    this.userKey = key;
    this.userId = userId;
  }

  /**
   * Retrieves the user's encryption key, generating a new one if necessary.
   * @example
   * getUserKey("user123").then(key => console.warn(key))
   * // Outputs user's encryption key, either fetched or newly generated
   * @param {string} userId - Identifier of the user to fetch or generate a key for.
   * @returns {Promise<string>} A promise that resolves with the user's encryption key.
   * @description
   *   - Uses supabase to query the user's existing encryption key from the database.
   *   - If no existing key is found, generates a new secure key and insertion record.
   *   - Handles errors related to database operations meticulously with informative error messages.
   *   - Supports both old and new encryption methods for backward compatibility.
   *   - Includes retry logic with exponential backoff for timeout scenarios.
   */
  async getUserKey(userId: string): Promise<string> {
    if (this.userId === userId && this.userKey) {
      return this.userKey;
    }

    const maxRetries = 3;
    const baseDelay = 1000; // 1 second
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt += 1) {
      try {
        return await fetchOrCreateUserKey(userId, this);
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        const isRetryable =
          isEncryptionTimeoutError(lastError.message) ||
          isEncryptionTransientServerError(lastError.message);
        if (!isRetryable || attempt >= maxRetries) {
          throwGetUserKeyError(lastError);
        }

        await retryGetUserKeyAfterDelay(attempt, maxRetries, baseDelay, lastError);
      }
    }

    // This should never be reached, but TypeScript needs it
    throw new Error(
      `Failed to get or create encryption key after ${maxRetries} attempts: ${lastError?.message || "Unknown error"}`
    );
  }

  clearCache() {
    this.userKey = null;
    this.userId = null;
  }
}

const encryptionManager = new UserEncryptionManager();

/**
 * Clear the encryption key cache (useful for testing or logout scenarios)
 */
export function clearEncryptionCache(): void {
  encryptionManager.clearCache();
}

/**
 * Encrypt data for a specific user
 */
export async function encryptForUser(
  text: string,
  userId: string
): Promise<{ cipher: string; iv: string }> {
  const userKey = await encryptionManager.getUserKey(userId);
  return encryptWithKey(text, userKey);
}

export type EncryptForUserResult = {
  cipher: string;
  iv?: string;
  encrypted: boolean;
};

/**
 * Encrypt when possible; on key provisioning failure, store plain text so saves still succeed.
 * Retries with cache clear and force key replace (QA Bug #5).
 */
export async function encryptForUserWithFallback(
  text: string,
  userId: string
): Promise<EncryptForUserResult> {
  try {
    const { cipher, iv } = await encryptForUser(text, userId);
    return { cipher, iv, encrypted: true };
  } catch (firstError) {
    try {
      clearEncryptionCache();
      const { cipher, iv } = await encryptForUser(text, userId);
      return { cipher, iv, encrypted: true };
    } catch {
      try {
        clearEncryptionCache();
        const userKey = await provisionEncryptionKey(userId, encryptionManager, {
          forceReplace: true,
        });
        const { cipher, iv } = encryptWithKey(text, userKey);
        return { cipher, iv, encrypted: true };
      } catch (replaceError) {
        reportEncryptionFallbackToSentry(replaceError ?? firstError, userId);
        if (process.env.NODE_ENV === "development") {
          console.warn(
            "[Encryption] Falling back to plain text storage:",
            replaceError ?? firstError
          );
        }
        return { cipher: text, encrypted: false };
      }
    }
  }
}

/**
 * Decrypt data for a specific user
 */
export async function decryptForUser(
  ciphertext: string,
  ivHex: string,
  userId: string
): Promise<string> {
  try {
    // ✅ ENHANCED: Validate inputs
    if (!ciphertext || !ivHex || !userId) {
      console.warn("decryptForUser: Missing required parameters");
      return "";
    }

    const userKey = await encryptionManager.getUserKey(userId);
    if (!userKey) {
      console.warn("decryptForUser: No user key available");
      return "";
    }

    return decryptWithKey(ciphertext, ivHex, userKey);
  } catch (error) {
    console.warn("User decryption failed:", error);
    return ""; // Return empty string instead of throwing
  }
}

/**
 * Legacy browser-derived key material for deprecated encrypt/decrypt helpers only.
 */
let legacyBrowserKeyMaterial: string | null = null;
function getLegacyBrowserKeyMaterial(): string {
  console.warn("DEPRECATED: getLegacyBrowserKeyMaterial() is insecure and should not be used");
  if (!legacyBrowserKeyMaterial) {
    const browserInfo = navigator.userAgent || "";
    const timezoneFactor = new Date().getTimezoneOffset();
    const domainFactor = window.location.hostname;
    const combinedFactors = `${domainFactor}-${timezoneFactor}-${browserInfo.length}`;
    const derivedKey = CryptoJS.SHA256(`${combinedFactors}-tellerwand-secure-key`).toString();
    legacyBrowserKeyMaterial = derivedKey.substring(0, 32);
  }
  return legacyBrowserKeyMaterial;
}

/**
 * @deprecated Use encryptForUser() instead - this method is insecure
 */
export function encrypt(text: string): { cipher: string; iv: string } {
  console.warn("DEPRECATED: encrypt() is insecure. Use encryptForUser() instead");
  const iv = CryptoJS.lib.WordArray.random(16);
  const key = getLegacyBrowserKeyMaterial();
  const cipher = CryptoJS.AES.encrypt(text, key, { iv }).toString();
  return {
    cipher,
    iv: iv.toString(CryptoJS.enc.Hex),
  };
}

/**
 * @deprecated Use decryptForUser() instead - this method is insecure
 */
export function decrypt(ciphertext: string, ivHex: string): string {
  console.warn("DEPRECATED: decrypt() is insecure. Use decryptForUser() instead");
  try {
    const iv = CryptoJS.enc.Hex.parse(ivHex);
    const key = getLegacyBrowserKeyMaterial();
    const bytes = CryptoJS.AES.decrypt(ciphertext, key, { iv });
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    console.error("Decryption failed:", error);
    return "";
  }
}

/**
 * Generate SHA-256 hash of the input string
 * @param text The text to hash
 * @param salt Optional salt to add to the hash for additional security
 * @returns SHA-256 hash as a hex string
 */
export function hashSha256(text: string, salt: string = ""): string {
  const textWithSalt = text + salt;
  return CryptoJS.SHA256(textWithSalt).toString(CryptoJS.enc.Hex);
}

export function verifySha256(text: string, storedHash: string, salt: string = ""): boolean {
  const computedHash = hashSha256(text, salt);
  return constantTimeCompare(computedHash, storedHash);
}

export function generateSalt(length: number = 16): string {
  return CryptoJS.lib.WordArray.random(length).toString(CryptoJS.enc.Hex);
}

export function constantTimeCompare(a: string, b: string): boolean {
  if (a.length !== b.length) return false;

  let result = 0;
  for (let i = 0; i < a.length; i += 1) {
    result |= (a.codePointAt(i) ?? 0) ^ (b.codePointAt(i) ?? 0);
  }

  return result === 0;
}

/**
 * Generate Security Token.
 */
export function generateSecurityToken(
  data: string,
  validityMinutes: number = 30
): { token: string; expiry: Date } {
  const expiry = new Date();
  expiry.setMinutes(expiry.getMinutes() + validityMinutes);

  const expiryTimestamp = expiry.getTime();
  const tokenData = `${data}|${expiryTimestamp}`;

  const token = hashSha256(tokenData, getLegacyBrowserKeyMaterial());

  return { token, expiry };
}

export function verifySecurityToken(token: string, data: string, expiry: Date): boolean {
  if (new Date() > expiry) return false;

  const expiryTimestamp = expiry.getTime();
  const tokenData = `${data}|${expiryTimestamp}`;
  const expectedToken = hashSha256(tokenData, getLegacyBrowserKeyMaterial());

  return constantTimeCompare(token, expectedToken);
}
